import type { ReactElement } from 'react';
import {
  BooleanInput,
  DateInput,
  DateTimeInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  minValue,
  required,
} from 'react-admin';
// `useWatch` vient de react-hook-form, que react-admin utilise pour ses
// formulaires mais ne reexporte pas. L'importer a la source est plus honnete
// qu'un chemin interne de react-admin, qui pourrait changer sans preavis.
import { useWatch } from 'react-hook-form';

import type { FieldDescriptor } from '@/resources/descriptors';

/**
 * Rendu en saisie d'un champ, deduit de son descripteur.
 *
 * Deux exigences que ce module tient et qu'un formulaire generique naif rate :
 *
 * 1. **La validation reflete le serveur, pas une supposition.** Un champ
 *    obligatoire cote serveur l'est ici ; un champ que le serveur ne lit meme pas
 *    n'est pas affiche. C'est ce qui evite le double echec habituel — exiger un
 *    champ inutile, ou laisser partir une requete refusee en `validation_failed`.
 * 2. **Les quantites sont positives.** Le domaine interdit un lot negatif
 *    (AGENTS.md) : la borne est verifiee cote serveur, et rappelee ici pour que
 *    l'utilisateur la voie avant l'envoi, jamais a la place du serveur.
 *
 * Les champs conditionnels (`visibleWhen`, `requiredWhen`) lisent les valeurs du
 * formulaire par `useWatch` : le formulaire d'alimentation change de forme selon
 * qu'on saisit un biberon ou une tetee, parce que ce sont deux routes de creation
 * distinctes.
 */

/** Unites rappelees dans l'aide, quand le descripteur ne le fait pas deja. */
const UNIT_HELPER: Partial<Record<FieldDescriptor['kind'], string>> = {
  milliliters: 'En millilitres.',
  grams: 'En grammes.',
  millimeters: 'En millimètres.',
  centimeters: 'En centimètres.',
  celsius: 'En degrés Celsius.',
  months: 'En mois.',
  seconds: 'En secondes.',
};

const QUANTITY_KINDS: ReadonlySet<FieldDescriptor['kind']> = new Set([
  'milliliters',
  'grams',
  'millimeters',
  'centimeters',
  'months',
  'seconds',
]);

/**
 * Conversion d'un instant saisi vers un instant **non ambigu**.
 *
 * Le défaut que ces deux fonctions corrigent, et qui serait passé inaperçu :
 *
 * `DateTimeInput` de React Admin est un `<input type="datetime-local">`. Il
 * soumet sa valeur **brute**, par exemple `2026-08-07T09:30` — sans fuseau et
 * sans secondes. Le backend, lui, fait
 * `new \DateTimeImmutable($value, new \DateTimeZone('UTC'))` : une chaîne sans
 * décalage est donc interprétée **en UTC**.
 *
 * Conséquence : un utilisateur à Paris (UTC+2 en été) qui saisit 09:30
 * enregistrerait un tirage à 09:30 UTC, soit 11:30 chez lui. Deux heures
 * d'écart, aucune erreur, aucun message — et « la date du tirage reste la
 * référence du lait » (AGENTS.md) ne serait plus vraie. Sur les durées de
 * conservation, l'écart se propage aux dates de péremption.
 *
 * `parse` convertit donc la saisie locale en instant ISO 8601 **avec `Z`**, ce
 * que le backend lit sans ambiguïté. `format` refait le chemin inverse pour que
 * le champ affiche l'heure locale de l'utilisateur, et non un décalage.
 */
const parseLocalDateTime = (value: unknown): string | null => {
  if (typeof value !== 'string' || value === '') {
    return null;
  }

  // Une chaîne date-heure sans décalage est interprétée en heure LOCALE par
  // ECMAScript — c'est exactement ce qu'a saisi l'utilisateur.
  const instant = new Date(value);

  // Une valeur illisible est transmise telle quelle plutôt que transformée en
  // `Invalid Date` : la validation du champ la refusera avec un message, là où un
  // `null` silencieux ferait croire à un champ vide. Le cas est rare — un
  // `datetime-local` rend `''` tant que la saisie est incomplète, jamais une
  // chaîne partielle — mais la valeur peut aussi venir de l'API.
  return Number.isNaN(instant.getTime()) ? value : instant.toISOString();
};

const formatLocalDateTime = (value: unknown): string => {
  if (typeof value !== 'string' || value === '') {
    return '';
  }

  const instant = new Date(value);

  if (Number.isNaN(instant.getTime())) {
    return '';
  }

  const pad = (part: number): string => String(part).padStart(2, '0');

  // Construit à la main plutôt que par `toISOString().slice(0, 16)` : cette
  // dernière rendrait l'heure UTC, ce qui réintroduirait exactement le décalage
  // que l'on corrige.
  return [
    String(instant.getFullYear()),
    '-',
    pad(instant.getMonth() + 1),
    '-',
    pad(instant.getDate()),
    'T',
    pad(instant.getHours()),
    ':',
    pad(instant.getMinutes()),
  ].join('');
};

export const dateTimeConversion = { parse: parseLocalDateTime, format: formatLocalDateTime };

/**
 * Enveloppe qui n'affiche son contenu que si le predicat accepte le formulaire.
 *
 * Un composant a part entiere, et non un `&&` dans la boucle de rendu : `useWatch`
 * est un hook, il ne peut pas etre appele conditionnellement depuis un `map`.
 */
const ConditionalInput = ({
  when,
  children,
}: {
  when: (values: Readonly<Record<string, unknown>>) => boolean;
  children: ReactElement;
}): ReactElement | null => {
  const values = useWatch<Record<string, unknown>>();

  return when(values) ? children : null;
};

/**
 * Champ dont l'obligation depend des autres valeurs.
 *
 * `validate` est recalcule a chaque rendu, ce qui est exactement voulu : le champ
 * devient obligatoire des que le type de seance change.
 */
const ConditionallyRequiredInput = ({
  when,
  render,
}: {
  when: (values: Readonly<Record<string, unknown>>) => boolean;
  render: (isRequired: boolean) => ReactElement;
}): ReactElement => {
  const values = useWatch<Record<string, unknown>>();

  return render(when(values));
};

/** Aide affichee sous le champ : celle du descripteur, sinon l'unite. */
const helperText = (descriptor: FieldDescriptor): string | undefined =>
  descriptor.helperText ?? UNIT_HELPER[descriptor.kind];

const buildInput = (descriptor: FieldDescriptor, isRequired: boolean): ReactElement => {
  const validators = [
    ...(isRequired ? [required()] : []),
    // Les quantites et durees du domaine ne sont jamais negatives. La borne
    // serveur reste seule juge ; celle-ci evite un aller-retour inutile.
    ...(QUANTITY_KINDS.has(descriptor.kind) ? [minValue(0)] : []),
  ];

  const common = {
    source: descriptor.name,
    label: descriptor.label,
    validate: validators.length > 0 ? validators : undefined,
    helperText: helperText(descriptor) ?? false,
    fullWidth: true,
  } as const;

  switch (descriptor.kind) {
    case 'boolean':
      return <BooleanInput {...common} />;

    case 'date':
      // Aucune conversion : une date seule est envoyée en `YYYY-MM-DD`, que le
      // backend relit en `Y-m-d`. La convertir en instant introduirait le
      // décalage de fuseau que l'on cherche justement à éviter, et ferait basculer
      // une date de naissance d'un jour.
      return <DateInput {...common} />;

    case 'dateTime':
      return <DateTimeInput {...common} {...dateTimeConversion} />;

    case 'choice':
      // L'option vide est l'absence de valeur : le serveur la lit comme `null`
      // (« non renseigné ») et c'est le seul moyen de vider le champ.
      return <SelectInput {...common} choices={[...(descriptor.choices ?? [])]} emptyText="Non précisé" />;

    case 'reference':
      // `ReferenceInput` interroge la ressource cible par le `dataProvider` : la
      // liste proposee est donc celle du profil bebe selectionne, jamais une
      // liste globale qui produirait un 403 a l'enregistrement.
      return descriptor.reference === undefined ? (
        <TextInput {...common} />
      ) : (
        <ReferenceInput source={descriptor.name} reference={descriptor.reference}>
          <SelectInput
            label={descriptor.label}
            validate={common.validate}
            helperText={common.helperText}
            fullWidth
            optionText={(record: Record<string, unknown>) =>
              typeof record.name === 'string' && record.name !== ''
                ? record.name
                : typeof record.code === 'string' && record.code !== ''
                  ? record.code
                  : String(record.id)
            }
          />
        </ReferenceInput>
      );

    case 'stringList':
      // Aucun composant React Admin ne saisit une liste de chaines sans en faire
      // une ressource liee. Une ligne par valeur est la forme la plus directe, et
      // `parse`/`format` gardent la conversion au bord du champ.
      return (
        <TextInput
          {...common}
          multiline
          minRows={2}
          format={(value: unknown) => (Array.isArray(value) ? value.join('\n') : (value ?? ''))}
          parse={(value: unknown) =>
            typeof value === 'string'
              ? value
                  .split('\n')
                  .map((entry) => entry.trim())
                  .filter((entry) => entry !== '')
              : []
          }
          helperText={descriptor.helperText ?? 'Une valeur par ligne.'}
        />
      );

    case 'longText':
      return <TextInput {...common} multiline minRows={3} />;

    case 'integer':
    case 'milliliters':
    case 'grams':
    case 'millimeters':
    case 'celsius':
    case 'months':
    case 'seconds':
      return <NumberInput {...common} />;

    // Saisi en centimetres, envoye en millimetres (#373) : seul kind dont la
    // valeur affichee n'est pas celle transmise au serveur.
    case 'centimeters':
      return (
        <NumberInput
          {...common}
          format={(value: unknown) => (typeof value === 'number' ? value / 10 : value)}
          parse={(value: unknown) =>
            typeof value === 'number' ? Math.round(value * 10) : value
          }
        />
      );

    case 'email':
      return <TextInput {...common} type="email" />;

    case 'identifier':
    case 'text':
    case 'json':
      return <TextInput {...common} />;
  }
};

/**
 * Saisie correspondant au descripteur, conditions comprises.
 *
 * Les enveloppes sont posees de l'exterieur vers l'interieur : la visibilite
 * d'abord — un champ masque n'a pas a etre valide — puis l'obligation.
 */
export const renderInput = (descriptor: FieldDescriptor): ReactElement => {
  const inner =
    descriptor.requiredWhen === undefined ? (
      buildInput(descriptor, descriptor.required === true)
    ) : (
      <ConditionallyRequiredInput
        when={descriptor.requiredWhen}
        render={(isRequired) => buildInput(descriptor, isRequired || descriptor.required === true)}
      />
    );

  if (descriptor.visibleWhen === undefined) {
    return <div key={descriptor.name}>{inner}</div>;
  }

  return (
    <ConditionalInput key={descriptor.name} when={descriptor.visibleWhen}>
      <div>{inner}</div>
    </ConditionalInput>
  ) as ReactElement;
};
